import { useMemo } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";

import { ITemplateEditorMergeTag } from "../../../models/TemplateEditor";

interface CustomTinyMceEditorProps {
  content: string;
  setContent: (value: string) => void;
  mergeTags: ITemplateEditorMergeTag[];
  mode: "html" | "text";
  height?: number;
  placeholder?: string;
  readonly?: boolean;
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const isFullHtmlDoc = (content: string) => /<html/i.test(content);

const extractBodyContent = (html: string) => {
  const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return match ? match[1] : html;
};

const injectBodyContent = (originalHtml: string, newBodyContent: string) =>
  originalHtml.replace(
    /(<body[^>]*>)([\s\S]*?)(<\/body>)/i,
    (_, open, _old, close) => `${open}${newBodyContent}${close}`
  );

const toEditorValue = (content: string, mode: "html" | "text") => {
  if (mode === "html") {
    if (isFullHtmlDoc(content)) return extractBodyContent(content);
    return content ?? "";
  }
  return escapeHtml(content ?? "").replace(/\r\n/g, "\n").replace(/\n/g, "<br />");
};

export default function CustomTinyMceEditor({
  content,
  setContent,
  mergeTags,
  mode,
  height = 380,
  placeholder = "Start typing...",
  readonly = false,
}: CustomTinyMceEditorProps) {
  const { t } = useTranslation("common");
  const init = useMemo(() => {
    const textMode = mode === "text";
    return {
      height,
      menubar: false,
      branding: false,
      statusbar: !readonly,
      promotion: false,
      placeholder: t(placeholder),
      forced_root_block: textMode ? "" : "p",
      toolbar: readonly
        ? false
        : textMode
          ? "undo redo | mergeTags"
          : "undo redo | styles | bold italic underline | bullist numlist | alignleft aligncenter alignright | link table | code | mergeTags",
      plugins: textMode ? "" : "link lists code table autolink",
      valid_elements: textMode ? "#text,br" : undefined,
      extended_valid_elements: textMode ? undefined : "input[type|id|name|class|value|checked|disabled]",
      setup: (editor: any) => {
        editor.ui.registry.addMenuButton("mergeTags", {
          text: t("Merge Tags"),
          fetch: (callback: any) => {
            const items = (mergeTags || []).map((mergeTag) => ({
              type: "menuitem",
              text: mergeTag.label,
              onAction: () => editor.insertContent(mergeTag.tag),
            }));
            callback(items);
          },
        });
      },
    };
  }, [mode, mergeTags, height, placeholder]);

  return (
    <Editor
      licenseKey="gpl"
      tinymceScriptSrc="/tinymce/tinymce.min.js"
      value={toEditorValue(content, mode)}
      disabled={readonly}
      init={init}
      onEditorChange={(newContent, editor) => {
        if (mode === "text") {
          setContent(editor.getContent({ format: "text" }));
          return;
        }

        if (isFullHtmlDoc(content)) {
          setContent(injectBodyContent(content, newContent));
          return;
        }

        setContent(newContent);
      }}
    />
  );
}
